'use client';

/**
 * AppNav -- sticky top navigation bar (desktop + mobile).
 *
 * Frosted glass background, coral brand logo, pill-style active nav links.
 * Hides on scroll-down, reappears on scroll-up.
 */

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { UserMenu } from '@/components/layout/user-menu';
import { cn } from '@/lib/utils';

const SIGNED_IN_LINKS = [
  { label: 'Home',    href: '/dashboard' },
  { label: 'Explore', href: '/explore' },
  { label: 'Library', href: '/library' },
] as const;

const SIGNED_OUT_LINKS = [
  { label: 'Explore', href: '/explore' },
] as const;

export function AppNav() {
  const { isSignedIn } = useUser();
  const pathname = usePathname();
  const [visible, setVisible] = useState(true);
  const [mounted, setMounted] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    function handleScroll() {
      const currentY = window.scrollY;
      // Always show when near top
      if (currentY < 10) {
        setVisible(true);
      } else if (currentY > lastScrollY.current) {
        setVisible(false); // scrolling down
      } else {
        setVisible(true);  // scrolling up
      }
      lastScrollY.current = currentY;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function isActive(href: string): boolean {
    return pathname === href || pathname.startsWith(href + '/');
  }

  return (
    <nav
      className={cn(
        'border-b border-border/50 bg-background/80 backdrop-blur-xl sticky top-0 z-50 transition-transform duration-300',
        visible ? 'translate-y-0' : '-translate-y-full'
      )}
    >
      <div className="mx-auto max-w-5xl px-6 flex h-14 items-center justify-between">
        <div className="flex items-center gap-8">
          <Link href="/" className="font-bold text-lg tracking-tight text-primary">
            favely
          </Link>
          <div className="flex items-center gap-1">
            {(isSignedIn ? SIGNED_IN_LINKS : SIGNED_OUT_LINKS).map(({ label, href }) => (
              <Link
                key={href}
                href={href}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  isActive(href)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                )}
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {mounted && isSignedIn ? (
            <UserMenu />
          ) : mounted && !isSignedIn ? (
            <div className="flex items-center gap-2">
              <Button asChild size="sm" variant="ghost" className="rounded-lg">
                <Link href="/sign-in">Sign in</Link>
              </Button>
              <Button asChild size="sm" className="rounded-lg">
                <Link href="/sign-up">Get started</Link>
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
